import React from 'react';
import { logCustomEvent } from '../services/firebase';
import { reportClientError } from '../services/errorReportingService';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorRef: null, errorTimestamp: null };
  }

  static getDerivedStateFromError() {
    const errorRef = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, errorRef, errorTimestamp: new Date().toLocaleString() };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });

    const errorRef = this.state.errorRef;

    // Log error to Firebase Analytics — wrapped in try/catch so telemetry
    // failures never break the error fallback UI
    try {
      logCustomEvent('app_error', {
        error_ref: errorRef,
        error_message: error?.message || 'Unknown error',
        error_stack: error?.stack?.substring(0, 500) || '',
        component_stack: errorInfo?.componentStack?.substring(0, 500) || '',
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    } catch (telemetryError) {
      // Silently ignore telemetry failures
    }

    // Forward to the dev_error Slack channel (devs only; best-effort, never throws).
    reportClientError({
      source: 'ErrorBoundary',
      message: error?.message || 'Unknown error',
      stack: error?.stack,
    });

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorRef: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const errorType = this.state.error?.name || 'Runtime Error';
      const route = window.location.pathname;
      const timestamp = this.state.errorTimestamp || new Date().toLocaleString();

      return (
        <div className="min-h-screen bg-[#0b0d2a] flex items-center justify-center px-4">
          <div className="max-w-lg w-full text-center">
            <div className="text-6xl mb-6 text-[#ff6b6b] select-none">&#9888;</div>

            <h1 className="text-2xl font-semibold text-white mb-3">
              Something Went Wrong
            </h1>

            <p className="text-gray-400 mb-6 leading-relaxed">
              An unexpected error occurred. We've been notified and are looking into it.
            </p>

            <div className="bg-[#0d0d1a] border border-gray-700 rounded-lg p-5 mb-8 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Error type</span>
                  <span className="text-gray-300">{errorType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Page</span>
                  <span className="text-gray-300 font-mono text-xs">{route}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Time</span>
                  <span className="text-gray-300">{timestamp}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Reference</span>
                  <span className="text-gray-300 font-mono text-xs">{this.state.errorRef}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-500 transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
              >
                Go to Home
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left mt-6">
                <summary className="cursor-pointer text-[#ff6b6b] text-sm hover:underline">
                  Error Details (Development Only)
                </summary>
                <pre className="bg-[#0d0d1a] p-4 rounded-lg overflow-auto text-xs text-gray-400 mt-2 max-h-64">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
